/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.4 — PARASHARI SHADBALA & BHAVA BALA CORE ENGINE
 * ============================================================================
 * Canonical Classical Authority:
 * - Brihat Parashara Hora Shastra (BPHS), Chapters 27, 28, and 29
 * 
 * Scope:
 * 1. Six-Fold Strength of 7 Classical Grahas (Shadbala):
 *    - Sthana Bala (Uccha, Saptavargaja across D1-D30, Ojhayugma, Kendradi, Drekkana)
 *    - Dig Bala (Continuous angular distance from Zero-Digbala Nirbala point)
 *    - Kaala Bala (Natonnatha day/night arc, Paksha lunar phase, Tribhaga 3-part day/night,
 *                  Dina/Hora/Masa/Varsha lords, Ayana declination/Kranti)
 *    - Cheshta Bala (Vakra / velocity / elongation dynamics; Sun/Moon classical baselines)
 *    - Naisargika Bala (Classical cosmic luminosity hierarchy: Sun > Moon > Ven > Jup > Mer > Mars > Sat)
 *    - Drik Bala (Net benefic minus malefic Drishti Virupas on planetary coordinates)
 *    - Total Virupas, Total Rupas, Classical Minimum Comparison, and Strength Categories
 *    - Classical Ishta Phala & Kashta Phala (BPHS Ch. 28 geometric formulation)
 * 2. Twelve House Strengths (Bhava Bala, BPHS Ch. 29):
 *    - Bhavadhipati Bala (Full lord Shadbala in Rupas)
 *    - Bhava Dig Bala (Directional strength by sign anatomical classification: Nara, Jalachara, Keeta, Chatushpada)
 *    - Bhava Drishti Bala (Algebraic sum of Drishti Virupas directly on house cusp degrees)
 *    - Total Bhava Bala, Category, and Rank (1 to 12)
 * ============================================================================
 */

import * as _Astronomy from 'astronomy-engine';
const Astronomy: any = (_Astronomy as any).MakeTime ? _Astronomy : ((_Astronomy as any).default || _Astronomy);
import {
  PlanetName,
  ZodiacSign,
  PlanetPosition,
  HouseInfo,
  KundaliChartData,
  ShadbalaItem,
  BhavaBalaItem,
  ShadbalaData,
  SthanaBalaBreakdown,
  KaalaBalaBreakdown,
  PanchangInfo,
  CheshtaEvidence
} from '../types/kundali';
import {
  NATURAL_FRIENDSHIPS,
  PLANET_OWN_SIGNS,
  getPanchadhaMaitriCompound
} from './planetaryDignityEngine';
import {
  calculateAuthoritativeSunTimes,
  calculateCanonicalVara,
  getCanonicalAyanamsha
} from './panchangaEngine';

// ============================================================================
// 1. CLASSICAL CONSTANTS & MINIMUM REQUIREMENTS (BPHS Ch. 27)
// ============================================================================

export const SEVEN_CLASSICAL_GRAHAS: PlanetName[] = [
  'Sun',
  'Moon',
  'Mars',
  'Mercury',
  'Jupiter',
  'Venus',
  'Saturn'
];

/**
 * Classical minimum required Rupas (BPHS Ch. 27, Verse 38)
 */
export const CLASSICAL_REQUIRED_RUPAS: Record<PlanetName, number> = {
  Sun: 6.5,     // 390 Virupas
  Moon: 6.0,    // 360 Virupas
  Mars: 5.0,    // 300 Virupas
  Mercury: 7.0, // 420 Virupas
  Jupiter: 6.5, // 390 Virupas
  Venus: 5.5,   // 330 Virupas
  Saturn: 5.0,  // 300 Virupas
  Rahu: 5.0,
  Ketu: 5.0,
  Ascendant: 5.0
};

/**
 * Fixed classical Naisargika Bala (BPHS Ch. 27, Verse 30)
 * Fixed Virupas strictly proportional to 60 * (7 - rank) / 7
 */
export const NAISARGIKA_VIRUPAS: Record<PlanetName, number> = {
  Sun: 60.00,
  Moon: 51.43,
  Venus: 42.86,
  Jupiter: 34.29,
  Mercury: 25.71,
  Mars: 17.14,
  Saturn: 8.57,
  Rahu: 0,
  Ketu: 0,
  Ascendant: 0
};

/**
 * Paramoccha (Deep Exaltation) Longitudes (BPHS Ch. 27, Verse 3)
 */
export const DEEP_EXALTATION_LONGITUDES: Record<PlanetName, number> = {
  Sun: 10,      // Aries 10°
  Moon: 33,     // Taurus 3°
  Mars: 298,    // Capricorn 28°
  Mercury: 165, // Virgo 15°
  Jupiter: 95,  // Cancer 5°
  Venus: 357,   // Pisces 27°
  Saturn: 200,  // Libra 20°
  Rahu: 50,     // Taurus 20°
  Ketu: 230,    // Scorpio 20°
  Ascendant: 0
};

/**
 * Moolatrikona Signs and Degree Spans in D1 (BPHS Ch. 3)
 */
export const MOOLATRIKONA_SPANS: Record<PlanetName, { signIndex: number; startDeg: number; endDeg: number }> = {
  Sun: { signIndex: 4, startDeg: 0, endDeg: 20 },       // Leo 0°-20°
  Moon: { signIndex: 1, startDeg: 3, endDeg: 30 },      // Taurus 3°-30°
  Mars: { signIndex: 0, startDeg: 0, endDeg: 12 },      // Aries 0°-12°
  Mercury: { signIndex: 5, startDeg: 15, endDeg: 20 },  // Virgo 15°-20°
  Jupiter: { signIndex: 8, startDeg: 0, endDeg: 10 },   // Sagittarius 0°-10°
  Venus: { signIndex: 6, startDeg: 0, endDeg: 15 },     // Libra 0°-15°
  Saturn: { signIndex: 10, startDeg: 0, endDeg: 20 },   // Aquarius 0°-20°
  Rahu: { signIndex: 10, startDeg: 0, endDeg: 20 },
  Ketu: { signIndex: 7, startDeg: 0, endDeg: 20 },
  Ascendant: { signIndex: 0, startDeg: 0, endDeg: 30 }
};

const ZODIAC_SIGNS: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_LORDS: Record<ZodiacSign, PlanetName> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

// ============================================================================
// 2. DIVISIONAL CHART CALCULATORS FOR SAPTAVARGAJA BALA (D1, D2, D3, D7, D9, D12, D30)
// ============================================================================

function getVargaSignIndex(planetLon: number, varga: 'D1' | 'D2' | 'D3' | 'D7' | 'D9' | 'D12' | 'D30'): number {
  const normLon = ((planetLon % 360) + 360) % 360;
  const signIdx = Math.floor(normLon / 30);
  const degInSign = normLon % 30;
  const isOdd = signIdx % 2 === 0; // 0=Aries (odd)

  switch (varga) {
    case 'D1':
      return signIdx;

    case 'D2': // Hora
      if (degInSign < 15) {
        return isOdd ? 4 : 3; // Leo (Sun) or Cancer (Moon)
      } else {
        return isOdd ? 3 : 4;
      }

    case 'D3': { // Drekkana
      const part = Math.floor(degInSign / 10); // 0, 1, 2
      return (signIdx + part * 4) % 12;
    }

    case 'D7': { // Saptamsha
      const part = Math.floor(degInSign / (30 / 7));
      const start = isOdd ? signIdx : (signIdx + 6) % 12;
      return (start + part) % 12;
    }

    case 'D9': { // Navamsha
      const navPart = Math.floor(degInSign / (30 / 9)); // 0 to 8
      const element = signIdx % 4; // 0=Fire, 1=Earth, 2=Air, 3=Water
      const startSigns = [0, 9, 6, 3]; // Aries, Cap, Libra, Cancer
      return (startSigns[element] + navPart) % 12;
    }

    case 'D12': { // Dwadasamsha
      const part = Math.floor(degInSign / 2.5); // 0 to 11
      return (signIdx + part) % 12;
    }

    case 'D30': { // Trishamsha
      if (isOdd) {
        if (degInSign < 5) return 0;   // Aries (Mars)
        if (degInSign < 10) return 10; // Aquarius (Saturn)
        if (degInSign < 18) return 8;  // Sagittarius (Jupiter)
        if (degInSign < 25) return 2;  // Gemini (Mercury)
        return 6;                      // Libra (Venus)
      } else {
        if (degInSign < 5) return 1;   // Taurus (Venus)
        if (degInSign < 12) return 5;  // Virgo (Mercury)
        if (degInSign < 20) return 11; // Pisces (Jupiter)
        if (degInSign < 25) return 9;  // Capricorn (Saturn)
        return 7;                      // Scorpio (Mars)
      }
    }
  }
}

// ============================================================================
// 3. STHANA BALA COMPUTATION (BPHS Ch. 27, Verses 2-14)
// ============================================================================

export function calculateUcchaBala(planet: PlanetName, longitude: number): number {
  const exaltationPoint = DEEP_EXALTATION_LONGITUDES[planet] ?? 0;
  const debilitationPoint = (exaltationPoint + 180) % 360;

  const diff = ((longitude - debilitationPoint + 360) % 360);
  const dist = diff > 180 ? 360 - diff : diff;

  // Maximum 60 Virupas at 180° distance from debilitation
  const ucchaVirupas = dist / 3;
  return Number(Math.max(0, Math.min(60, ucchaVirupas)).toFixed(2));
}

export function calculateSaptavargajaBala(
  planet: PlanetName,
  planetLon: number,
  allPlanets: PlanetPosition[]
): number {
  const vargas: ('D1' | 'D2' | 'D3' | 'D7' | 'D9' | 'D12' | 'D30')[] = [
    'D1', 'D2', 'D3', 'D7', 'D9', 'D12', 'D30'
  ];

  let totalSaptavarga = 0;

  for (const varga of vargas) {
    const planetVargaSign = getVargaSignIndex(planetLon, varga);
    const signName = ZODIAC_SIGNS[planetVargaSign];
    const signLord = SIGN_LORDS[signName];

    // 1. Check Moolatrikona in D1
    if (varga === 'D1') {
      const mt = MOOLATRIKONA_SPANS[planet];
      const degInSign = planetLon % 30;
      if (mt && planetVargaSign === mt.signIndex && degInSign >= mt.startDeg && degInSign <= mt.endDeg) {
        totalSaptavarga += 45;
        continue;
      }
    }

    // 2. Check Own Sign
    const ownSigns = PLANET_OWN_SIGNS[planet] || [];
    if (ownSigns.includes(signName)) {
      totalSaptavarga += 30;
      continue;
    }

    // 3. Compound Friendship (Panchadha Maitri) with sign lord in this varga
    const naturalRel = NATURAL_FRIENDSHIPS[planet]?.friends.includes(signLord)
      ? 'Friend'
      : NATURAL_FRIENDSHIPS[planet]?.enemies.includes(signLord)
      ? 'Enemy'
      : 'Neutral';

    // Find lord's position in natal D1 chart (BPHS Ch. 15 / Ch. 27):
    // Tatkalika Maitri is strictly determined by physical planetary positions in the birth chart (D1).
    const lordObj = allPlanets.find(p => p.name === signLord);
    const planetD1Sign = Math.floor(planetLon / 30);
    const lordD1Sign = lordObj ? Math.floor(lordObj.longitude / 30) : planetD1Sign;
    
    // Temporal relationship: 2, 3, 4, 10, 11, 12 from planet in D1 are Friends; 1, 5, 6, 7, 8, 9 are Enemies
    const natalHouseDiff = ((lordD1Sign - planetD1Sign + 12) % 12) + 1;
    const isTemporalFriend = [2, 3, 4, 10, 11, 12].includes(natalHouseDiff);
    const temporalRel: 'Friend' | 'Enemy' = isTemporalFriend ? 'Friend' : 'Enemy';

    const compound = getPanchadhaMaitriCompound(naturalRel, temporalRel);

    switch (compound) {
      case 'Great Friend':
        totalSaptavarga += 20;
        break;
      case 'Friend':
        totalSaptavarga += 15;
        break;
      case 'Neutral':
        totalSaptavarga += 10;
        break;
      case 'Enemy':
        totalSaptavarga += 3.75;
        break;
      case 'Great Enemy':
        totalSaptavarga += 1.875;
        break;
    }
  }

  return Number(totalSaptavarga.toFixed(2));
}

export function calculateOjhayugmarashiBala(
  planet: PlanetName,
  planetLon: number
): number {
  const d1Sign = getVargaSignIndex(planetLon, 'D1');
  const d9Sign = getVargaSignIndex(planetLon, 'D9');

  const d1IsOdd = d1Sign % 2 === 0; // Aries=0 (odd sign)
  const d9IsOdd = d9Sign % 2 === 0;

  let points = 0;

  // Male planets (Sun, Mars, Jupiter) & Mercury get 15 Virupas in Odd signs
  if (['Sun', 'Mars', 'Jupiter', 'Mercury'].includes(planet)) {
    if (d1IsOdd) points += 15;
    if (d9IsOdd) points += 15;
  } else {
    // Female planets (Moon, Venus) & Saturn get 15 Virupas in Even signs
    if (!d1IsOdd) points += 15;
    if (!d9IsOdd) points += 15;
  }

  return points;
}

export function calculateKendradiBala(houseNumber: number): number {
  if ([1, 4, 7, 10].includes(houseNumber)) return 60;
  if ([2, 5, 8, 11].includes(houseNumber)) return 30;
  return 15; // 3, 6, 9, 12
}

export function calculateDrekkanaBala(planet: PlanetName, degreeInSign: number): number {
  const decanate = Math.floor(degreeInSign / 10); // 0 = 1st, 1 = 2nd, 2 = 3rd

  if (decanate === 0 && ['Sun', 'Mars', 'Jupiter'].includes(planet)) {
    return 15; // 1st Drekkana: Male planets
  }
  if (decanate === 1 && ['Mercury', 'Saturn'].includes(planet)) {
    return 15; // 2nd Drekkana: Neutral/Hermaphrodite planets
  }
  if (decanate === 2 && ['Moon', 'Venus'].includes(planet)) {
    return 15; // 3rd Drekkana: Female planets
  }

  return 0;
}

export function calculateCompleteSthanaBala(
  planet: PlanetName,
  planetPos: PlanetPosition,
  allPlanets: PlanetPosition[]
): SthanaBalaBreakdown {
  const uchhaBala = calculateUcchaBala(planet, planetPos.longitude);
  const saptavargiyaBala = calculateSaptavargajaBala(planet, planetPos.longitude, allPlanets);
  const ojayugmaBala = calculateOjhayugmarashiBala(planet, planetPos.longitude);
  const kendraBala = calculateKendradiBala(planetPos.house);
  const drekanaBala = calculateDrekkanaBala(planet, planetPos.degreeInSign);

  const total = Number((uchhaBala + saptavargiyaBala + ojayugmaBala + kendraBala + drekanaBala).toFixed(2));

  return {
    uchhaBala,
    saptavargiyaBala,
    ojayugmaBala,
    kendraBala,
    drekanaBala,
    total
  };
}

// ============================================================================
// 4. DIG BALA COMPUTATION (BPHS Ch. 27, Verses 15-18)
// ============================================================================

export function calculateDigBala(
  planet: PlanetName,
  planetLon: number,
  housesOrAscendant: HouseInfo[] | number
): number {
  let ascDeg = 0;
  if (typeof housesOrAscendant === 'number') {
    ascDeg = ((housesOrAscendant % 360) + 360) % 360;
  } else if (Array.isArray(housesOrAscendant)) {
    const h1 = housesOrAscendant.find(h => h.houseNumber === 1);
    if (h1?.bhavaMadhya !== undefined) {
      ascDeg = h1.bhavaMadhya;
    } else if (h1) {
      ascDeg = h1.degreeStart;
    }
  }

  // Peak (60 Virupa) and Nirbala (0 Virupa) reference points (BPHS Ch. 27, v. 15-18):
  // Jupiter/Mercury: peak House 1 (Asc), nirbala House 7 (Asc + 180°)
  // Sun/Mars: peak House 10 (Asc + 270°), nirbala House 4 (Asc + 90°)
  // Saturn: peak House 7 (Asc + 180°), nirbala House 1 (Asc)
  // Moon/Venus: peak House 4 (Asc + 90°), nirbala House 10 (Asc + 270°)
  let nirbalaLon = 0;
  if (planet === 'Jupiter' || planet === 'Mercury') {
    nirbalaLon = (ascDeg + 180) % 360;
  } else if (planet === 'Sun' || planet === 'Mars') {
    nirbalaLon = (ascDeg + 90) % 360;
  } else if (planet === 'Saturn') {
    nirbalaLon = ascDeg % 360;
  } else if (planet === 'Moon' || planet === 'Venus') {
    nirbalaLon = (ascDeg + 270) % 360;
  }

  const diff = Math.abs(planetLon - nirbalaLon);
  const dist = diff > 180 ? 360 - diff : diff;

  // Maximum 60 Virupas at 180° distance from Nirbala point
  return Number(Math.max(0, Math.min(60, dist / 3)).toFixed(2));
}

// ============================================================================
// 5. KAALA BALA COMPUTATION (BPHS Ch. 27, Verses 15-28)
// ============================================================================

const meshaSankrantiCache = new Map<number, Date>();

/**
 * Calculates the exact timestamp of Mesha Sankranti (sidereal Sun ingress into Aries 0°)
 * for a given calendar year using high-precision astronomical binary search.
 */
export function getMeshaSankrantiDate(year: number): Date {
  const cached = meshaSankrantiCache.get(year);
  if (cached) return cached;

  let low = new Date(Date.UTC(year, 3, 12, 0, 0, 0)).getTime();
  let high = new Date(Date.UTC(year, 3, 16, 0, 0, 0)).getTime();

  for (let i = 0; i < 20; i++) {
    const mid = (low + high) / 2;
    const time = Astronomy.MakeTime(new Date(mid));
    const sunEcl = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Sun, time, true));
    const ayan = getCanonicalAyanamsha(time, 'lahiri');
    const siderealLon = ((sunEcl.elon - ayan) % 360 + 360) % 360;

    if (siderealLon > 180) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const result = new Date((low + high) / 2);
  meshaSankrantiCache.set(year, result);
  return result;
}

/**
 * Determines the Lord of the Year (Varshesha) from the Vedic weekday of the Mesha Sankranti
 * immediately preceding the birth date (BPHS Ch. 27, v. 24).
 */
export function getPrecedingMeshaSankrantiLord(birthDate: Date): PlanetName {
  const year = birthDate.getFullYear();
  const currentYearSankranti = getMeshaSankrantiDate(year);
  let targetSankranti = currentYearSankranti;

  if (birthDate.getTime() < currentYearSankranti.getTime()) {
    targetSankranti = getMeshaSankrantiDate(year - 1);
  }

  const weekdayLords: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  return weekdayLords[targetSankranti.getUTCDay()];
}

const VARA_STRING_TO_PLANET: Record<string, PlanetName> = {
  sunday: 'Sun',
  ravivara: 'Sun',
  ravi: 'Sun',
  adityavara: 'Sun',
  surya: 'Sun',
  monday: 'Moon',
  somavara: 'Moon',
  soma: 'Moon',
  chandra: 'Moon',
  tuesday: 'Mars',
  mangalavara: 'Mars',
  mangala: 'Mars',
  bhauma: 'Mars',
  bhaumavara: 'Mars',
  wednesday: 'Mercury',
  budhavara: 'Mercury',
  budha: 'Mercury',
  thursday: 'Jupiter',
  guruvara: 'Jupiter',
  guru: 'Jupiter',
  brihaspativara: 'Jupiter',
  friday: 'Venus',
  shukravara: 'Venus',
  shukra: 'Venus',
  saturday: 'Saturn',
  shanivara: 'Saturn',
  shani: 'Saturn'
};

export const DIURNAL_GRAHAS: PlanetName[] = ['Sun', 'Jupiter', 'Venus'];
export const NOCTURNAL_GRAHAS: PlanetName[] = ['Moon', 'Mars', 'Saturn'];

export function calculateCompleteKaalaBala(
  planet: PlanetName,
  planetPos: PlanetPosition,
  sunPos: PlanetPosition,
  moonPos: PlanetPosition,
  optionsOrDate: ShadbalaCalculationOptions | Date
): KaalaBalaBreakdown {
  if (!optionsOrDate) {
    throw new Error('Shadbala calculation requires an explicit astronomical datetime (options.birthDate). Silent fallbacks to arbitrary epochs or system time are forbidden.');
  }

  let targetDate: Date;
  let options: ShadbalaCalculationOptions;

  if (optionsOrDate instanceof Date) {
    if (isNaN(optionsOrDate.getTime())) {
      throw new Error('Shadbala calculation requires an explicit astronomical datetime (valid Date object).');
    }
    targetDate = optionsOrDate;
    options = { birthDate: optionsOrDate };
  } else if (optionsOrDate && optionsOrDate.birthDate instanceof Date && !isNaN(optionsOrDate.birthDate.getTime())) {
    targetDate = optionsOrDate.birthDate;
    options = optionsOrDate;
  } else {
    throw new Error('Shadbala calculation requires an explicit astronomical datetime (options.birthDate). Silent fallbacks to arbitrary epochs or system time are forbidden.');
  }

  const lat = options.latitude ?? 28.6139;
  const lon = options.longitude ?? 77.2090;

  // Astronomical Sun Times
  let sunrise: Date;
  let sunset: Date;
  let solarNoon: Date;

  if (options.sunTimes) {
    sunrise = options.sunTimes.sunrise;
    sunset = options.sunTimes.sunset;
    solarNoon = options.sunTimes.solarNoon;
  } else if (options.latitude !== undefined && options.longitude !== undefined) {
    const st = calculateAuthoritativeSunTimes(targetDate, lat, lon);
    sunrise = st.sunrise;
    sunset = st.sunset;
    solarNoon = st.solarNoon;
  } else {
    // Fallback: civil 06:00 / 18:00
    const dStart = new Date(Date.UTC(targetDate.getUTCFullYear(), targetDate.getUTCMonth(), targetDate.getUTCDate()));
    sunrise = new Date(dStart.getTime() + 6 * 3600 * 1000);
    sunset = new Date(dStart.getTime() + 18 * 3600 * 1000);
    solarNoon = new Date(dStart.getTime() + 12 * 3600 * 1000);
  }

  const birthMs = targetDate.getTime();
  const riseMs = sunrise.getTime();
  const setMs = sunset.getTime();
  const noonMs = solarNoon.getTime();

  const dinamanaMs = Math.max(1, setMs - riseMs);
  const nextRiseMs = riseMs + 24 * 3600 * 1000;
  const ratrimanaMs = Math.max(1, nextRiseMs - setMs);
  const midnightMs = setMs + ratrimanaMs / 2;

  const isDay = birthMs >= riseMs && birthMs < setMs;

  // 1. Natonnatha Bala (Day/Night arc anchored to astronomical sun times)
  let arcFraction = 0;
  if (isDay) {
    const tMidday = Math.abs(birthMs - noonMs);
    arcFraction = Math.min(1, tMidday / (dinamanaMs / 2));
  } else {
    // Night births: either post-sunset or pre-sunrise
    let tMidnight = 0;
    if (birthMs >= setMs) {
      tMidnight = Math.abs(birthMs - midnightMs);
    } else {
      const prevMidnightMs = riseMs - ratrimanaMs / 2;
      tMidnight = Math.abs(birthMs - prevMidnightMs);
    }
    arcFraction = Math.min(1, tMidnight / (ratrimanaMs / 2));
  }

  let natonnathaBala = 30.00;
  if (planet === 'Mercury') {
    natonnathaBala = 60.00; // Mercury always receives 60 Virupas (BPHS Ch. 27, v. 20)
  } else if (DIURNAL_GRAHAS.includes(planet)) {
    // Diurnal planets: peak at local solar noon (60V), zero at midnight (0V), 30V at horizon
    natonnathaBala = isDay
      ? 60 - 30 * arcFraction
      : 30 * arcFraction;
  } else {
    // Nocturnal planets (Moon, Mars, Saturn): peak at midnight (60V), zero at noon (0V), 30V at horizon
    natonnathaBala = !isDay
      ? 60 - 30 * arcFraction
      : 30 * arcFraction;
  }
  natonnathaBala = Math.max(0, Math.min(60, natonnathaBala));

  // 2. Paksha Bala (Moon-Sun elongation)
  const elongation = ((moonPos.longitude - sunPos.longitude + 360) % 360);
  const pakshaArc = elongation > 180 ? 360 - elongation : elongation;
  const beneficPaksha = (pakshaArc / 180) * 60;

  let pakshaBala = 30;
  if (['Jupiter', 'Venus', 'Moon'].includes(planet)) {
    pakshaBala = beneficPaksha;
  } else if (planet === 'Mercury') {
    pakshaBala = beneficPaksha;
  } else {
    // Malefics (Sun, Mars, Saturn)
    pakshaBala = 60 - beneficPaksha;
  }

  // 3. Tribhaga Bala (3 portions of Dinamana and Ratrimana, BPHS Ch. 27, v. 19-20)
  let tribhagaBala = 0;
  if (planet === 'Jupiter') {
    tribhagaBala = 60.00; // Jupiter always receives 60 Virupas at all times
  } else if (isDay) {
    const dayPortion = Math.min(2, Math.max(0, Math.floor((birthMs - riseMs) / (dinamanaMs / 3))));
    if (dayPortion === 0 && planet === 'Mercury') tribhagaBala = 60;
    else if (dayPortion === 1 && planet === 'Sun') tribhagaBala = 60;
    else if (dayPortion === 2 && planet === 'Saturn') tribhagaBala = 60;
  } else {
    let nightOffset = 0;
    if (birthMs >= setMs) {
      nightOffset = birthMs - setMs;
    } else {
      nightOffset = birthMs - (riseMs - ratrimanaMs);
    }
    const nightPortion = Math.min(2, Math.max(0, Math.floor(nightOffset / (ratrimanaMs / 3))));
    if (nightPortion === 0 && planet === 'Moon') tribhagaBala = 60;
    else if (nightPortion === 1 && planet === 'Venus') tribhagaBala = 60;
    else if (nightPortion === 2 && planet === 'Mars') tribhagaBala = 60;
  }

  // 4. Dina Adhipati (Weekday Lord: 45 Virupas anchored to sunrise)
  let dinaLord: PlanetName;
  if (options.canonicalVara && VARA_STRING_TO_PLANET[options.canonicalVara.toLowerCase()]) {
    dinaLord = VARA_STRING_TO_PLANET[options.canonicalVara.toLowerCase()];
  } else if (options.panchang?.vara && VARA_STRING_TO_PLANET[options.panchang.vara.toLowerCase()]) {
    dinaLord = VARA_STRING_TO_PLANET[options.panchang.vara.toLowerCase()];
  } else {
    const v = calculateCanonicalVara(targetDate, sunrise);
    dinaLord = v.rulingPlanet;
  }
  const dinaBala = planet === dinaLord ? 45.00 : 0.00;

  // 5. Hora Adhipati (Planetary Hour Lord: 60 Virupas, sunrise anchored)
  const HORA_ORDER: PlanetName[] = ['Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars'];
  const dinaLordIndex = HORA_ORDER.indexOf(dinaLord);
  const startIdx = dinaLordIndex >= 0 ? dinaLordIndex : 0;

  let horaSlot = 0;
  if (isDay) {
    const horaDuration = dinamanaMs / 12;
    horaSlot = Math.min(11, Math.max(0, Math.floor((birthMs - riseMs) / horaDuration)));
  } else {
    const horaDuration = ratrimanaMs / 12;
    let nightOffset = 0;
    if (birthMs >= setMs) {
      nightOffset = birthMs - setMs;
    } else {
      nightOffset = birthMs - (riseMs - ratrimanaMs);
    }
    horaSlot = 12 + Math.min(11, Math.max(0, Math.floor(nightOffset / horaDuration)));
  }
  const currentHoraLord = HORA_ORDER[(startIdx + horaSlot) % 7];
  const horaBala = planet === currentHoraLord ? 60.00 : 0.00;

  // 6. Masa Adhipati (Month Lord / Sun sign ruler: 30 Virupas)
  const sunSign = ZODIAC_SIGNS[Math.floor(sunPos.longitude / 30)];
  const masaLord = SIGN_LORDS[sunSign];
  const masaBala = planet === masaLord ? 30.00 : 0.00;

  // 7. Varsha Adhipati (Solar Year Lord from Mesha Sankranti: 15 Virupas)
  const varshaLord = getPrecedingMeshaSankrantiLord(targetDate);
  const varshaBala = planet === varshaLord ? 15.00 : 0.00;

  // 8. Ayana Bala (Declination / Kranti strength with Tropical Sayana coordinates)
  const ayanamsha = options.ayanamshaDegrees ?? 23.85;
  const sayanaLon = ((planetPos.longitude + ayanamsha) % 360 + 360) % 360;
  const rad = Math.PI / 180;
  const obliquityDeg = 23.4392911;
  const declination = Math.asin(Math.sin(sayanaLon * rad) * Math.sin(obliquityDeg * rad)) / rad;

  let ayanaBala = 30.00;
  if (['Sun', 'Mars', 'Jupiter', 'Venus', 'Mercury'].includes(planet)) {
    // Northern declination (+deg) is strong (peak 60 at +24°)
    ayanaBala = Math.max(0, Math.min(60, (24.0 + declination) * 1.25));
  } else {
    // Moon, Saturn: Southern declination (-deg) is strong (peak 60 at -24°)
    ayanaBala = Math.max(0, Math.min(60, (24.0 - declination) * 1.25));
  }

  const yuddhaBala = 0; // 0 unless confirmed planetary war

  const total = Number((
    natonnathaBala + pakshaBala + tribhagaBala +
    dinaBala + horaBala + masaBala + varshaBala +
    ayanaBala + yuddhaBala
  ).toFixed(2));

  return {
    natonnathaBala: Number(natonnathaBala.toFixed(2)),
    pakshaBala: Number(pakshaBala.toFixed(2)),
    tribhagaBala,
    varshaBala,
    masaBala,
    dinaBala,
    horaBala,
    ayanaBala: Number(ayanaBala.toFixed(2)),
    yuddhaBala,
    total
  };
}

// ============================================================================
// 6. CHESHTA BALA COMPUTATION (BPHS Ch. 27, Verses 29-33 — METHOD A)
// ============================================================================

/**
 * Classical Ashtavidha Cheshta Virupa mapping (BPHS Ch. 27, Verses 29-33)
 */
export const ASHTAVIDHA_CHESHTA_VIRUPAS: Record<string, number> = {
  Vakra: 60,      // Retrograde motion (perigee / maximum motional vitality)
  Anuvakra: 30,   // Post-retrograde resumption / returning
  Vikala: 15,     // Stationary / turning
  Mandatara: 30,  // Extremely slow direct motion
  Manda: 15,      // Slow direct motion
  Sama: 7.5,      // Mean direct motion
  Chara: 45,      // Fast direct motion
  Atichara: 30    // Hyper-accelerated direct motion
};

/**
 * Classical Mean Daily Speeds in degrees/day (Surya Siddhanta Spashtadhikara 2.51-54 / BPHS Ch. 27)
 */
export const MEAN_DAILY_MOTIONS: Record<PlanetName, number> = {
  Mars: 0.524,
  Mercury: 1.383,
  Jupiter: 0.083,
  Venus: 1.200,
  Saturn: 0.033,
  Sun: 0.9856,
  Moon: 13.176,
  Rahu: 0.053,
  Ketu: 0.053,
  Ascendant: 360
};

export type AshtavidhaState =
  | 'Vakra'
  | 'Anuvakra'
  | 'Vikala'
  | 'Mandatara'
  | 'Manda'
  | 'Sama'
  | 'Chara'
  | 'Atichara';

export interface AshtavidhaClassificationResult {
  state: AshtavidhaState;
  dailyMotion: number;
  velocityRatio: number;
  virupas: number;
  reason: string;
  evidence: CheshtaEvidence;
}

function buildCheshtaEvidence(
  state: AshtavidhaState,
  virupas: number,
  dailyMotion: number,
  meanSpeed: number,
  velocityRatio: number
): CheshtaEvidence {
  return {
    state,
    virupas,
    dailyMotion,
    referenceMeanMotion: meanSpeed,
    velocityRatio,
    classicalRule: 'BPHS Ch. 27, v. 29-33 defines eight qualitative Cheshta motion states and Virupas (Vakra 60, Anuvakra 30, Vikala 15, Mandatara 30, Manda 15, Sama 7.5, Chara 45, Atichara 30).',
    astronomicalCalculation: `Apparent geocentric daily motion derived from astronomical ephemeris (v = ${dailyMotion.toFixed(4)}°/day).`,
    classificationMethod: `Velocity-ratio quantization against Surya Siddhantic mean motion (v̄ = ${meanSpeed.toFixed(4)}°/day, ratio = ${velocityRatio.toFixed(2)}).`,
    methodologyClassification: 'Hybrid Classical/Astronomical',
    quantizationNote: 'Parashari Jyotisha qualitatively defines the motion states; VedangJyotisham operationalizes them via continuous astronomical ephemeris velocity ratios.'
  };
}

/**
 * Deterministically classifies planetary apparent daily motion into one of the 8 BPHS Ashtavidha states.
 * Classical authority: BPHS Ch. 27, v. 29-33 (8 states & Virupas); Surya Siddhanta Spashtadhikara 2.51-54 (mean daily speeds).
 * Methodology: Category C — Hybrid Classical/Astronomical (discrete velocity-ratio thresholds map continuous ephemeris motion).
 */
export function classifyAshtavidhaMotion(
  planetName: PlanetName,
  dailyMotion: number
): AshtavidhaClassificationResult {
  const meanSpeed = MEAN_DAILY_MOTIONS[planetName];
  if (!meanSpeed || meanSpeed <= 0) {
    const state: AshtavidhaState = 'Sama';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Sama;
    return {
      state,
      dailyMotion,
      velocityRatio: 1,
      virupas,
      reason: 'Non-Tara Graha baseline',
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, 1, 1)
    };
  }

  // 1. Stationary Turn (Vikala): |v| <= 0.10 * meanSpeed (Category C: Hybrid model mapping near-stationary motion to BPHS Vikala 15V)
  if (Math.abs(dailyMotion) <= 0.10 * meanSpeed) {
    const state: AshtavidhaState = 'Vikala';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Vikala;
    const ratio = dailyMotion / meanSpeed;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Stationary pivot motion (|v| = ${dailyMotion.toFixed(4)}°/d <= 0.10 * mean)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }

  // 2. Retrograde motions (v < 0)
  if (dailyMotion < 0) {
    // Anuvakra: slow retrograde near turning points (|v| < 0.5 * meanSpeed)
    if (Math.abs(dailyMotion) < 0.5 * meanSpeed) {
      const state: AshtavidhaState = 'Anuvakra';
      const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Anuvakra;
      const ratio = dailyMotion / meanSpeed;
      return {
        state,
        dailyMotion,
        velocityRatio: ratio,
        virupas,
        reason: `Decelerating/returning retrograde (|v| = ${Math.abs(dailyMotion).toFixed(4)}°/d < 0.5 * mean)`,
        evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
      };
    }
    // Vakra: full retrograde motion (|v| >= 0.5 * meanSpeed)
    const state: AshtavidhaState = 'Vakra';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Vakra;
    const ratio = dailyMotion / meanSpeed;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Full retrograde motion (v = ${dailyMotion.toFixed(4)}°/d)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }

  // 3. Direct motions (v > 0)
  const ratio = dailyMotion / meanSpeed;

  if (ratio <= 0.5) {
    const state: AshtavidhaState = 'Mandatara';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Mandatara;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Very slow direct motion (ratio = ${ratio.toFixed(2)} <= 0.5)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }
  if (ratio <= 0.8) {
    const state: AshtavidhaState = 'Manda';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Manda;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Slow direct motion (0.10 < ratio = ${ratio.toFixed(2)} <= 0.8)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }
  if (ratio <= 1.1) {
    const state: AshtavidhaState = 'Sama';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Sama;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Normal/mean direct motion (0.8 < ratio = ${ratio.toFixed(2)} <= 1.1)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }
  if (ratio <= 1.3) {
    const state: AshtavidhaState = 'Chara';
    const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Chara;
    return {
      state,
      dailyMotion,
      velocityRatio: ratio,
      virupas,
      reason: `Swift direct motion (1.1 < ratio = ${ratio.toFixed(2)} <= 1.3)`,
      evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
    };
  }
  const state: AshtavidhaState = 'Atichara';
  const virupas = ASHTAVIDHA_CHESHTA_VIRUPAS.Atichara;
  return {
    state,
    dailyMotion,
    velocityRatio: ratio,
    virupas,
    reason: `Accelerated direct motion (ratio = ${ratio.toFixed(2)} > 1.3)`,
    evidence: buildCheshtaEvidence(state, virupas, dailyMotion, meanSpeed, ratio)
  };
}

export function calculateCheshtaBala(
  planet: PlanetName,
  planetPos: PlanetPosition,
  kaalaBreakdown: KaalaBalaBreakdown
): number {
  // Classical Rule (BPHS Ch. 27, v. 29):
  // Sun's Cheshta Bala is its Ayana Bala
  if (planet === 'Sun') {
    return kaalaBreakdown.ayanaBala;
  }
  // Moon's Cheshta Bala is its Paksha Bala
  if (planet === 'Moon') {
    return kaalaBreakdown.pakshaBala;
  }

  // For the 5 Tara Grahas (Mars, Mercury, Jupiter, Venus, Saturn):
  // Method A — BPHS Ashtavidha Cheshta (BPHS Ch. 27, v. 29-33)
  if (planetPos.ashtavidhaState && ASHTAVIDHA_CHESHTA_VIRUPAS[planetPos.ashtavidhaState] !== undefined) {
    return ASHTAVIDHA_CHESHTA_VIRUPAS[planetPos.ashtavidhaState];
  }

  // If dailyMotion is available, classify dynamically:
  if (planetPos.dailyMotion !== undefined) {
    const classification = classifyAshtavidhaMotion(planet, planetPos.dailyMotion);
    return classification.virupas;
  }

  if (planetPos.isRetrograde) {
    return ASHTAVIDHA_CHESHTA_VIRUPAS.Vakra; // 60 Virupas
  }

  // Classical mean direct motion baseline
  return ASHTAVIDHA_CHESHTA_VIRUPAS.Sama; // 7.5 Virupas
}

// ============================================================================
// 7. DRIK BALA COMPUTATION (Benefic minus Malefic Drishti Virupas)
// ============================================================================

function calculateAspectVirupas(diffAngle: number, aspectingPlanet: PlanetName): number {
  const theta = ((diffAngle % 360) + 360) % 360;

  // General 7th aspect and intermediate angles (BPHS Ch. 27, v. 34-37)
  let baseAspect = 0;
  if (theta >= 30 && theta < 60) {
    baseAspect = (theta - 30) / 2;
  } else if (theta >= 60 && theta < 90) {
    baseAspect = 15 + (theta - 60);
  } else if (theta >= 90 && theta < 120) {
    baseAspect = 45 - (theta - 90) / 2;
  } else if (theta >= 120 && theta < 150) {
    baseAspect = 30 - (theta - 120);
  } else if (theta >= 150 && theta < 180) {
    baseAspect = (theta - 150) * 2; // Full 60 at 180°
  } else if (theta >= 180 && theta < 300) {
    baseAspect = (300 - theta) / 2;
  }

  // Special aspects:
  if (aspectingPlanet === 'Mars' && ((theta >= 90 && theta <= 120) || (theta >= 210 && theta <= 240))) {
    baseAspect = Math.max(baseAspect, 60); // Full aspect on 4th and 8th
  } else if (aspectingPlanet === 'Jupiter' && ((theta >= 120 && theta <= 150) || (theta >= 240 && theta <= 270))) {
    baseAspect = Math.max(baseAspect, 60); // Full aspect on 5th and 9th
  } else if (aspectingPlanet === 'Saturn' && ((theta >= 60 && theta <= 90) || (theta >= 270 && theta <= 300))) {
    baseAspect = Math.max(baseAspect, 60); // Full aspect on 3rd and 10th
  }

  return Math.min(60, Math.max(0, baseAspect));
}

export function calculateDrikBala(
  targetPlanet: PlanetName,
  targetLon: number,
  allPlanets: PlanetPosition[]
): number {
  let beneficAspects = 0;
  let maleficAspects = 0;

  const benefics: PlanetName[] = ['Jupiter', 'Venus'];
  const malefics: PlanetName[] = ['Sun', 'Mars', 'Saturn'];

  for (const asp of allPlanets) {
    if (asp.name === targetPlanet || asp.name === 'Ascendant' || asp.name === 'Rahu' || asp.name === 'Ketu') {
      continue;
    }

    const diff = ((targetLon - asp.longitude + 360) % 360);
    const aspectVirupas = calculateAspectVirupas(diff, asp.name);

    if (benefics.includes(asp.name)) {
      beneficAspects += aspectVirupas;
    } else if (malefics.includes(asp.name)) {
      maleficAspects += aspectVirupas;
    } else if (asp.name === 'Mercury') {
      // Mercury is benefic unless combust (within 3° of Sun)
      const sun = allPlanets.find(p => p.name === 'Sun');
      const isCombust = sun && Math.abs(asp.longitude - sun.longitude) < 3;
      if (isCombust) maleficAspects += aspectVirupas;
      else beneficAspects += aspectVirupas;
    } else if (asp.name === 'Moon') {
      // Waxing Moon is benefic, Waning Moon is malefic
      const sun = allPlanets.find(p => p.name === 'Sun');
      const elongation = sun ? ((asp.longitude - sun.longitude + 360) % 360) : 180;
      if (elongation >= 90 && elongation <= 270) beneficAspects += aspectVirupas;
      else maleficAspects += aspectVirupas;
    }
  }

  // Drik Bala is net (Benefic - Malefic) / 4
  const drikVirupas = (beneficAspects - maleficAspects) / 4;
  return Number(drikVirupas.toFixed(2));
}

// ============================================================================
// 8. ISHTA PHALA & KASHTA PHALA (BPHS Ch. 28 Geometric Formulation)
// ============================================================================

export function calculateIshtaKashtaPhala(
  ucchaBala: number,
  cheshtaBala: number
): { ishtaPhala: number; kashtaPhala: number } {
  // Ishta Phala = sqrt(Uccha Bala * Cheshta Bala)
  const ishta = Math.sqrt(Math.max(0, ucchaBala * cheshtaBala));
  // Kashta Phala = sqrt((60 - Uccha Bala) * (60 - Cheshta Bala))
  const kashta = Math.sqrt(Math.max(0, (60 - ucchaBala) * (60 - cheshtaBala)));

  return {
    ishtaPhala: Number(Math.min(60, ishta).toFixed(2)),
    kashtaPhala: Number(Math.min(60, kashta).toFixed(2))
  };
}

// ============================================================================
// 9. BHAVA BALA COMPUTATION (BPHS Ch. 29)
// ============================================================================

export function calculateBhavaDigBala(signIndex: number, houseNumber: number, cuspDegree?: number): number {
  // Anatomical Classification (BPHS Ch. 29, v. 4):
  // Nara (Human): Gemini (2), Virgo (5), Libra (6), 1st half of Sagittarius (8, 0°-15°), Aquarius (10) -> Peak in House 1
  // Jalachara (Watery): Cancer (3), Pisces (11), 2nd half of Cap (9, 15°-30°) -> Peak in House 4
  // Keeta (Insect): Scorpio (7) -> Peak in House 7
  // Chatushpada (Quadruped): Aries (0), Taurus (1), Leo (4), 2nd half of Sag (8, 15°-30°), 1st half of Cap (9, 0°-15°) -> Peak in House 10
  let peakHouse = 1;

  const degInSign = cuspDegree !== undefined ? ((cuspDegree % 30) + 30) % 30 : 15;

  if ([2, 5, 6, 10].includes(signIndex)) {
    peakHouse = 1; // Nara
  } else if ([3, 11].includes(signIndex)) {
    peakHouse = 4; // Jalachara
  } else if (signIndex === 7) {
    peakHouse = 7; // Keeta
  } else if ([0, 1, 4].includes(signIndex)) {
    peakHouse = 10; // Chatushpada
  } else if (signIndex === 8) {
    // Sagittarius: 1st half Nara (H1), 2nd half Chatushpada (H10)
    peakHouse = degInSign < 15 ? 1 : 10;
  } else if (signIndex === 9) {
    // Capricorn: 1st half Chatushpada (H10), 2nd half Jalachara (H4)
    peakHouse = degInSign < 15 ? 10 : 4;
  }

  const diff = Math.abs(houseNumber - peakHouse);
  const deltaH = diff > 6 ? 12 - diff : diff;

  // Maximum 1.0 Rupa (60 Virupas) at peak house
  return Number((1.0 * (1 - deltaH / 6)).toFixed(2));
}

export function calculateBhavaDrishtiBala(
  cuspDegree: number,
  allPlanets: PlanetPosition[]
): number {
  let beneficAspects = 0;
  let maleficAspects = 0;

  const benefics: PlanetName[] = ['Jupiter', 'Venus'];
  const malefics: PlanetName[] = ['Sun', 'Mars', 'Saturn'];
  const sun = allPlanets.find(p => p.name === 'Sun');

  for (const asp of allPlanets) {
    if (asp.name === 'Ascendant' || asp.name === 'Rahu' || asp.name === 'Ketu') continue;

    const diff = ((cuspDegree - asp.longitude + 360) % 360);
    const aspectVirupas = calculateAspectVirupas(diff, asp.name);

    if (benefics.includes(asp.name)) {
      beneficAspects += aspectVirupas;
    } else if (malefics.includes(asp.name)) {
      maleficAspects += aspectVirupas;
    } else if (asp.name === 'Mercury') {
      // Mercury is benefic unless combust (within 3° of Sun)
      const isCombust = sun && Math.abs(asp.longitude - sun.longitude) < 3;
      if (isCombust) maleficAspects += aspectVirupas;
      else beneficAspects += aspectVirupas;
    } else if (asp.name === 'Moon') {
      // Waxing Moon is benefic, Waning Moon is malefic
      const elongation = sun ? ((asp.longitude - sun.longitude + 360) % 360) : 180;
      if (elongation >= 90 && elongation <= 270) beneficAspects += aspectVirupas;
      else maleficAspects += aspectVirupas;
    }
  }

  // Converted to Rupas: Virupas / (4 * 60)
  const drishtiVirupas = (beneficAspects - maleficAspects) / 4;
  return Number((drishtiVirupas / 60).toFixed(2));
}

export function calculateCompleteBhavaBala(
  houses: HouseInfo[],
  planetItems: ShadbalaItem[],
  allPlanets: PlanetPosition[]
): BhavaBalaItem[] {
  const items: BhavaBalaItem[] = houses.map(h => {
    const lordShad = planetItems.find(s => s.planet === h.signLord);
    const lordBalaRupas = lordShad ? lordShad.totalRupas : 5.5;

    const targetDegree = h.bhavaMadhya !== undefined ? h.bhavaMadhya : h.degreeStart;
    const digBalaRupas = calculateBhavaDigBala(h.signIndex, h.houseNumber, targetDegree);
    const drishtiBalaRupas = calculateBhavaDrishtiBala(targetDegree, allPlanets);

    const totalRupas = Number((lordBalaRupas + digBalaRupas + drishtiBalaRupas).toFixed(2));

    let category: BhavaBalaItem['category'] = 'Moderate';
    if (totalRupas >= 7.5) category = 'Strong';
    else if (totalRupas < 6.0) category = 'Weak';

    return {
      houseNumber: h.houseNumber,
      sign: h.sign,
      signLord: h.signLord as PlanetName,
      lordBalaRupas,
      digBalaRupas,
      drishtiBalaRupas,
      totalRupas,
      category
    };
  });

  // Assign Internal Analytical Ranking (1 to 12)
  // NOTE: Labelled strictly as an internal computational feature per Layer 7.2A, not BPHS scriptural canon
  const sorted = [...items].sort((a, b) => b.totalRupas - a.totalRupas);
  sorted.forEach((item, idx) => {
    const target = items.find(h => h.houseNumber === item.houseNumber);
    if (target) target.rank = idx + 1;
  });

  return items;
}

// ============================================================================
// 10. CANONICAL SHADBALA MASTER ENTRY POINT
// ============================================================================

export interface ShadbalaCalculationOptions {
  birthDate: Date; // MANDATORY: Explicit astronomical anchor datetime for Kaala Bala
  latitude?: number;
  longitude?: number;
  panchang?: PanchangInfo;
  charts?: Record<string, KundaliChartData>;
  sunTimes?: { sunrise: Date; sunset: Date; solarNoon: Date };
  canonicalVara?: string;
  ayanamshaDegrees?: number;
}


export function calculateShadbala(
  planets: PlanetPosition[],
  houses: HouseInfo[],
  options: ShadbalaCalculationOptions
): ShadbalaData {
  if (!options || !options.birthDate || !(options.birthDate instanceof Date) || isNaN(options.birthDate.getTime())) {
    throw new Error('Shadbala calculation requires an explicit astronomical datetime (options.birthDate). Silent fallbacks to arbitrary epochs or system time are forbidden.');
  }

  const sunPos = planets.find(p => p.name === 'Sun') || { longitude: 0, house: 1, degreeInSign: 0 } as PlanetPosition;
  const moonPos = planets.find(p => p.name === 'Moon') || { longitude: 90, house: 4, degreeInSign: 0 } as PlanetPosition;

  const planetItems: ShadbalaItem[] = SEVEN_CLASSICAL_GRAHAS.map(pName => {
    const pPos = planets.find(p => p.name === pName) || {
      name: pName,
      longitude: 0,
      house: 1,
      degreeInSign: 0,
      isRetrograde: false
    } as PlanetPosition;

    // 1. Sthana Bala
    const sthanaBreakdown = calculateCompleteSthanaBala(pName, pPos, planets);
    const sthanaBala = sthanaBreakdown.total;

    // 2. Dig Bala
    const digBala = calculateDigBala(pName, pPos.longitude, houses);

    // 3. Kaala Bala
    const kaalaBreakdown = calculateCompleteKaalaBala(pName, pPos, sunPos, moonPos, options);
    const kaalaBala = kaalaBreakdown.total;

    // 4. Cheshta Bala
    const cheshtaBala = calculateCheshtaBala(pName, pPos, kaalaBreakdown);

    // 5. Naisargika Bala
    const naisargikaBala = NAISARGIKA_VIRUPAS[pName] ?? 30;

    // 6. Drik Bala
    const drikBala = calculateDrikBala(pName, pPos.longitude, planets);

    // Aggregates
    const totalVirupas = Number((sthanaBala + digBala + kaalaBala + cheshtaBala + naisargikaBala + drikBala).toFixed(2));
    const totalRupas = Number((totalVirupas / 60).toFixed(2));
    const requiredRupas = CLASSICAL_REQUIRED_RUPAS[pName] ?? 6.0;
    const ratio = Number((totalRupas / requiredRupas).toFixed(2));

    // Strength Category
    let strengthCategory: ShadbalaItem['strengthCategory'] = 'Moderate';
    if (ratio >= 1.25) strengthCategory = 'Very Strong';
    else if (ratio >= 1.0) strengthCategory = 'Strong';
    else if (ratio < 0.85) strengthCategory = 'Weak';

    // Ishta / Kashta Phala
    const { ishtaPhala, kashtaPhala } = calculateIshtaKashtaPhala(sthanaBreakdown.uchhaBala, cheshtaBala);

    // Cheshta Evidence Metadata (F-L7.5-02)
    let cheshtaEvidence: CheshtaEvidence | undefined;
    if (pName === 'Sun') {
      cheshtaEvidence = {
        state: 'Sama',
        virupas: cheshtaBala,
        classicalRule: 'BPHS Ch. 27, v. 29: Sun Cheshta Bala equals its Ayana Bala.',
        astronomicalCalculation: 'Equatorial declination (Kranti) converted from tropical longitude.',
        classificationMethod: 'Classical solar identity (Cheshta = Ayana Bala).',
        methodologyClassification: 'Hybrid Classical/Astronomical',
        quantizationNote: 'Direct classical identity.'
      };
    } else if (pName === 'Moon') {
      cheshtaEvidence = {
        state: 'Sama',
        virupas: cheshtaBala,
        classicalRule: 'BPHS Ch. 27, v. 29: Moon Cheshta Bala equals its Paksha Bala.',
        astronomicalCalculation: 'Solar-lunar elongation arc from New Moon to Full Moon.',
        classificationMethod: 'Classical lunar identity (Cheshta = Paksha Bala).',
        methodologyClassification: 'Hybrid Classical/Astronomical',
        quantizationNote: 'Direct classical identity.'
      };
    } else if (pPos.ashtavidhaState && ASHTAVIDHA_CHESHTA_VIRUPAS[pPos.ashtavidhaState] !== undefined) {
      const mean = MEAN_DAILY_MOTIONS[pName] ?? 1.0;
      const v = pPos.dailyMotion ?? 0;
      cheshtaEvidence = {
        state: pPos.ashtavidhaState,
        virupas: cheshtaBala,
        dailyMotion: pPos.dailyMotion,
        referenceMeanMotion: mean,
        velocityRatio: mean > 0 ? Number((Math.abs(v) / mean).toFixed(3)) : 1.0,
        classicalRule: 'BPHS Ch. 27, v. 29-33 defines eight qualitative Cheshta motion states and Virupas (Vakra 60, Anuvakra 30, Vikala 15, Mandatara 30, Manda 15, Sama 7.5, Chara 45, Atichara 30).',
        astronomicalCalculation: `Apparent geocentric daily motion derived from astronomical ephemeris (v = ${v.toFixed(4)}°/day).`,
        classificationMethod: `Velocity-ratio quantization against Surya Siddhantic mean speed (v̄ = ${mean.toFixed(4)}°/day).`,
        methodologyClassification: 'Hybrid Classical/Astronomical',
        quantizationNote: 'Parashari Jyotisha qualitatively defines the motion states; VedangJyotisham operationalizes them using an astronomical velocity-ratio quantization model.'
      };
    }

    return {
      planet: pName,
      sthanaBala,
      sthanaBreakdown,
      digBala,
      kaalaBala,
      kaalaBreakdown,
      cheshtaBala,
      cheshtaEvidence,
      naisargikaBala,
      drikBala,
      totalVirupas,
      totalRupas,
      requiredRupas,
      ratio,
      // Backward compatibility aliases
      shadbalaRatio: ratio,
      totalShadbalaVirupas: totalVirupas,
      strengthCategory,
      ishtaPhala,
      kashtaPhala
    };
  });

  // Calculate Bhava Bala for 12 Houses
  const bhavaBalaList = calculateCompleteBhavaBala(houses, planetItems, planets);

  // Strongest and Weakest
  const sortedByRatio = [...planetItems].sort((a, b) => b.ratio - a.ratio);
  const strongestPlanet = sortedByRatio[0].planet;
  const weakestPlanet = sortedByRatio[sortedByRatio.length - 1].planet;

  return {
    planets: planetItems,
    bhavaBala: bhavaBalaList,
    strongestPlanet,
    weakestPlanet
  };
}

